import { IPlacement } from "@sp-crm/core";
import { Panel } from "components/ui/panel/panel";
import { PanelType } from "components/ui/panel/panel-type";
import {
    ClientTransferRequestStatus,
    GetPendingOutgoingClientTransfersQuery,
    useFindClientTransferRequestByClientIdQuery,
} from "generated/graphql";
import React, { useCallback } from "react";
import { useProductName } from "store/selectors/branding";
import { useCurrentTenant } from "store/selectors/hooks";
import { OutgoingTransferCard } from "../transfers/outgoing-transfer-card";
import { QueryRenderer } from "./community-comparison/query-renderer";
import { SelectAgentForClientTransfer } from "./select-agent-for-client-transfer";

interface TransferClientPanelProps {
    placement: IPlacement;
    onDismiss: () => void;
}

export const TransferClientPanel: React.FC<TransferClientPanelProps> = props => {
    const { placement, onDismiss } = props;
    const tenant = useCurrentTenant();
    const productName = useProductName();
    const query = useFindClientTransferRequestByClientIdQuery({
        clientId: placement.id,
    });

    const onCancelled = useCallback(async () => {
        await query.refetch();
    }, [query]);

    const onTransferred = useCallback(async () => {
        await query.refetch();
    }, [query]);

    return (
        <Panel
            type={PanelType.large}
            headerText={`Transfer client ${placement.preferredClientName}`}
            onDismiss={onDismiss}
            isOpen={true}>
            <div className="space-y-4">
                <QueryRenderer query={query} name="findClientTransferRequestByClientId">
                    {data => {
                        if (
                            data.findClientTransferRequestByClientId?.status ===
                            ClientTransferRequestStatus.Pending
                        ) {
                            return (
                                <>
                                    <p>
                                        There is a transfer in progress to{" "}
                                        {
                                            data.findClientTransferRequestByClientId
                                                .toUserProfile.name
                                        }
                                        , an advisor outside of {tenant.name}.
                                    </p>
                                    <OutgoingTransferCard
                                        clientTransferRequest={
                                            data.findClientTransferRequestByClientId as unknown as GetPendingOutgoingClientTransfersQuery["getPendingOutgoingClientTransfers"][number]
                                        }
                                        onCancelled={onCancelled}
                                    />
                                </>
                            );
                        }

                        if (
                            data.findClientTransferRequestByClientId?.status ===
                            ClientTransferRequestStatus.Accepted
                        ) {
                            return (
                                <>
                                    <p>
                                        This client has been transferred to{" "}
                                        {
                                            data.findClientTransferRequestByClientId
                                                .toUserProfile.name
                                        }
                                        , an advisor outside of {tenant.name}.
                                    </p>
                                </>
                            );
                        }
                        return (
                            <>
                                <p>
                                    Request to transfer this client to another{" "}
                                    {productName} customer.
                                </p>

                                <SelectAgentForClientTransfer
                                    placement={placement}
                                    onTransferred={onTransferred}
                                />
                            </>
                        );
                    }}
                </QueryRenderer>
            </div>
        </Panel>
    );
};
